import { IconButton, Tooltip } from '@mui/material'
import { EyeIcon, EyeOffIcon, LockIcon } from 'lucide-react'

/**
 * The row controls shared by this page's two certificate-curation cards —
 * `ProductCustomFieldsCard` (VRG-101/104, the brand's own fields) and
 * `CertificateFieldsCard` (VRG-788, the standard ones).
 *
 * Extracted on the SECOND hand-roll rather than copied (working-conventions §36). The
 * two cards sit one above the other on the same page, so a divergence between them —
 * a different button size, a different "on" colour, an eye that means shown in one and
 * hidden in the other — would be visible in a single screenshot. Local to this page
 * rather than promoted to `components/`: nothing else in the platform curates
 * certificate field visibility, and a shared component with one surface is a guess
 * about the second.
 */

/**
 * 40×40 borderless icon button — a bigger tap target at input height (matching the
 * +/- steppers' footprint) with no border or fill chrome, so a row's controls read as
 * light text-buttons rather than boxed ones.
 */
export const ACTION_BTN_SX = {
  width: 40,
  height: 40,
  borderRadius: '8px',
  color: 'text.secondary',
  '&:hover': { backgroundColor: 'action.hover' },
  '&:disabled': { color: 'grey.300' },
} as const

interface VisibilityToggleProps {
  /** Is the field currently shown on the certificate? */
  visible: boolean
  onToggle?: () => void
  /**
   * Set for a field the brand CANNOT hide. The control renders shown-and-disabled and
   * this becomes its tooltip.
   *
   * §46: a disabled control must carry a reason the reader can read, and the forcing
   * function is that you have to be able to write the sentence. A locked row with no
   * reason is a row that should not have been locked.
   */
  lockReason?: string
}

/**
 * Shown / hidden, as one 40×40 eye — or a LOCK on a row the brand cannot change.
 *
 * The brand colour on the "shown" state is the whole legibility of it: both eye icons
 * are the same weight and size, so an all-grey column of eyes and crossed-out eyes is
 * read glyph by glyph. Colour makes the shown rows scannable as a group.
 *
 * ## Why the locked rows get a different GLYPH, not just a disabled eye
 *
 * A greyed-out eye and an active eye are the same symbol at two opacities, so the
 * locked rows read as "shown, and this control is unavailable right now" — which
 * invites the reader to wonder what would make it available. Nothing would. A lock says
 * the state is not a setting, in the one glance the row gets, and it says it without
 * depending on opacity, which is the weakest signal in the UI and the first one lost on
 * a dim screen.
 *
 * It also stops the column lying about what it contains. With eyes throughout, the
 * column read as seven toggles of which four happened to be off-limits; with locks
 * first and eyes after, it reads as two groups, which is what it is.
 *
 * The lock is NOT tinted brand. Brand here means "shown because you chose it", and a
 * locked row is shown whatever you choose — colouring it the same would merge the two
 * groups back together at exactly the distance the colour was added to separate them.
 */
export const VisibilityToggle = ({ visible, onToggle, lockReason }: VisibilityToggleProps) => {
  const locked = !!lockReason
  const title = locked
    ? lockReason
    : visible
      ? 'Shown on certificate'
      : 'Hidden from certificate'

  return (
    <Tooltip title={title}>
      {/* A disabled MUI IconButton fires no pointer events, so the tooltip would never
          open on the locked rows — which are precisely the ones whose tooltip carries the
          only explanation. The span is the hover target. */}
      <span>
        <IconButton
          disabled={locked}
          onClick={locked ? undefined : onToggle}
          sx={{
            ...ACTION_BTN_SX,
            ...(visible && !locked && { color: 'brand.600' }),
          }}
        >
          {locked ? (
            <LockIcon strokeWidth={1} size={18} />
          ) : visible ? (
            <EyeIcon strokeWidth={1} size={18} />
          ) : (
            <EyeOffIcon strokeWidth={1} size={18} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  )
}
